'use client';

import * as React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

import { Typography } from '@components/typography';
import { Icon, IconNames } from '@components/icon';

type Props = {
  /**
   * Image src for a phone mockup PNG. The image is expected to include the
   * device frame (notch, bezel, side buttons) baked in with a transparent
   * background; the component just renders it cleanly with a soft drop
   * shadow + fade-up entrance animation.
   *
   * The iOS waitlist surfaces currently pass
   * `/assets/img/ios-app/home-screen-device.png` (a mobile preview of the
   * Savvy home page rendered inside an iPhone shell) as a stand-in until
   * the real iOS app screenshot is ready.
   */
  screenSrc?: string;
  /** Alt text for the phone image. Required when `screenSrc` is set. */
  screenAlt?: string;
  /** Override the outer wrapper sizing (defaults to a responsive ~280px). */
  className?: string;
};

const DEVICE_ASPECT_RATIO = '413 / 844';

/**
 * Phone mockup used by the iOS waitlist surfaces. Renders a device-framed
 * image (or a flat on-brand placeholder when no `screenSrc` is provided)
 * with a soft drop shadow and a one-shot framer-motion fade-up.
 *
 * Client Component only because of the framer-motion mount animation; it can
 * still be embedded inside Server Components.
 */
export const PhoneMockup: React.FC<Props> = ({
  screenSrc,
  screenAlt,
  className,
}) => {
  const wrapperClassName = twMerge(
    'relative mx-auto w-[260px] sm:w-[280px]',
    className
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={wrapperClassName}
      data-id="phone-mockup"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-6 -bottom-4 h-10 rounded-full bg-primary-main/25 blur-2xl"
      />

      <div
        className="relative w-full drop-shadow-2xl"
        style={{ aspectRatio: DEVICE_ASPECT_RATIO }}
      >
        {screenSrc ? (
          <Image
            src={screenSrc}
            alt={screenAlt ?? ''}
            fill
            className="object-contain"
            sizes="(max-width: 640px) 260px, 280px"
            priority
          />
        ) : (
          <PlaceholderDevice />
        )}
      </div>
    </motion.div>
  );
};

const PlaceholderDevice: React.FC = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-[2rem] bg-gradient-to-b from-pool via-primary-xlight to-white text-center shadow-inner ring-1 ring-primary-dark/20">
    <span
      className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-main text-white shadow-lg"
      aria-hidden="true"
    >
      <Icon name={IconNames.MOBILE} size="xl" />
    </span>
    <Typography component="span" variant="title4" className="text-primary-main">
      Savvy
    </Typography>
    <Typography
      component="span"
      variant="small"
      className="uppercase tracking-wide text-primary-main/70"
    >
      Coming soon
    </Typography>
  </div>
);
